import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { useState } from "react";
import { Button } from "~/atoms/button";
import { Card, CardContent } from "~/atoms/card";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { cn } from "~/lib/utils";

interface ReorderItem {
  id: number;
  title: string;
}

interface Props<T extends ReorderItem> {
  items: T[];
  onChange: (items: T[]) => void;
  sortableType: string;
}

type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

export function SortableReorderList<T extends ReorderItem>({
  items,
  onChange,
  sortableType,
}: Props<T>) {
  const [animatingId, setAnimatingId] = useState<number | null>(null);
  const maxTitleLen =
    items.length > 0 ? Math.max(...items.map((item) => item.title.length)) : 0;

  const reorderByIndex = (fromIndex: number, toIndex: number) => {
    onChange(moveItemByIndex(items, fromIndex, toIndex));
  };

  const moveItem = (itemId: number, offset: -1 | 1) => {
    const fromIndex = items.findIndex((item) => item.id === itemId);
    reorderByIndex(fromIndex, fromIndex + offset);
    setAnimatingId(itemId);
    setTimeout(() => setAnimatingId(null), 300);
  };

  const handleDragEnd: OnDragEnd = (event) => {
    if (event.canceled || !isSortableOperation(event.operation)) return;

    const source = event.operation.source;
    if (!source) return;

    reorderByIndex(source.initialIndex, source.index);
  };

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <div className="grid auto-rows-max gap-4">
        {items.map((item, index) => (
          <SortableReorderRow
            key={item.id}
            item={item}
            index={index}
            maxTitleLen={maxTitleLen}
            animatingId={animatingId}
            onUp={(itemId) => moveItem(itemId, -1)}
            onDown={(itemId) => moveItem(itemId, 1)}
            total={items.length}
            sortableType={sortableType}
          />
        ))}
      </div>
    </DragDropProvider>
  );
}

const SortableReorderRow = ({
  item,
  index,
  maxTitleLen,
  animatingId,
  onUp,
  onDown,
  total,
  sortableType,
}: {
  item: ReorderItem;
  index: number;
  maxTitleLen: number;
  animatingId: number | null;
  onUp: (itemId: number) => void;
  onDown: (itemId: number) => void;
  total: number;
  sortableType: string;
}) => {
  const { ref, isDragging } = useSortable({
    id: `${item.id}`,
    index,
    type: sortableType,
    accept: sortableType,
  });

  return (
    <Card
      ref={ref}
      data-bump={animatingId === item.id}
      className={cn(
        "relative py-0 transition-all duration-200 data-[bump=true]:scale-[1.03] data-[bump=true]:shadow-lg",
        isDragging &&
          "border-primary/50 bg-primary/5 scale-[0.97] border-2 border-dashed opacity-50",
      )}
    >
      <CardContent className="flex items-center justify-between">
        <span
          className="text-muted-foreground/70 hover:text-muted-foreground cursor-grab select-none"
          aria-hidden="true"
          title="Drag"
        >
          <GripVertical className="size-4" />
        </span>
        <div
          className="text-sm font-medium"
          style={{ width: `${maxTitleLen}ch` }}
        >
          {item.title}
        </div>
        <div className="flex flex-col gap-1">
          <Button
            size="xs"
            variant="ghost"
            disabled={index === 0}
            onClick={() => onUp(item.id)}
          >
            <ChevronUp />
          </Button>
          <Button
            size="xs"
            variant="ghost"
            disabled={index === total - 1}
            onClick={() => onDown(item.id)}
          >
            <ChevronDown />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
